import { Component, Input, OnInit } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { Donation } from "../donation/donation";
import { OptionService } from "../option/option.service";
import { TranslateService } from "@ngx-translate/core";


@Component({
    selector: 'thankyou-order-text',
    templateUrl: './thankyou-order.text.component.html',
    providers: [DecimalPipe]
})
export class ThankyouOrderTextComponent implements OnInit {
    /**
     * The current donation
     */
    @Input() donation: Donation;

    /**
     * the current step
     */
    @Input() step: string;

    /**
     * Type of the project
     */
    @Input() projectType: string;

    /**
     * calculate the donation value for each month, independend from the donation rhythm
     *
     * @var {number}
     */
    donationPerMonth: number;

    /**
     * calculate the the donation value for each year, independend from the donation rhythm
     *
     * @var {number}
     */
    donationPerYear: number;

    /**
     * calculate the donation value for each half, independend from the donation rhythm
     *
     * @var {number}
     */
    donationPerhalfYear: number;

    /**
     * text for the rythm placeholder in the thankyou text
     */
    rythmText: string;

    /**
     * Special thank you message that should be visible
     */
    eventThankYou: any = false;

    /**
     * hides the thankyou text content
     */
    hidden = false;

    /**
     * textkey for the result page
     */
    textkey: string;

    /**
     * placeholders for the texts
     */
    placeholder: any;

    /**
     * Constructor
     * @param optionService
     * @param translate
     * @param decimalPipe
     */
    constructor(private optionService: OptionService, private translate: TranslateService, private decimalPipe: DecimalPipe) { }

    /**
     * called when initializing the component
     */
    ngOnInit() {
        // on event projects we get the thank you message from the creater
        if (this.projectType === "event") {
            this.eventThankYou = this.optionService.getEventOption('text_thankyou');
        }

        // init placeholder
        this.placeholder = this.getPlaceholders();

        // init textkey
        this.textkey = 'thankyou.text.end';
        if (this.step !== 'end') {
          this.textkey = 'thankyou.text.' + this.mapPaymentMethod(this.donation.paymentMethod);

          // check if we should hide the text
          this.translate.get(this.textkey).subscribe(data => {
            if (data.trim().length === 0) {
              this.hidden = true;
            }
          });
        }
    }

    /**
     * Map some payment methods
     * @param paymentMethod
     */
    mapPaymentMethod(paymentMethod: string): string {
      switch (paymentMethod) {
        case 'stripe_creditcard':
          return 'sixpay';
        case 'stripe_google_pay':
          return 'google_pay';
        case 'stripe_apple_pay':
          return 'apple_pay';
        case 'paypal_guest':
          return 'paypal';
      }
      return paymentMethod;
    }

    /**
     * function for providing placeholder data for the thank you page
     *
     * @return {Object}
     */
    getPlaceholders(): any {
        // check if reccurring donation
        if (this.donation.recurringRythm) {
            // placeholder for the thankyou text
            this.rythmText = this.translate.instant('rythm.recurring.' + this.donation.recurringRythm + '.label')

            // based on the rhytm we calculate some more placeholders
            switch (this.donation.recurringRythm) {
                case 'yearly':
                    this.donationPerMonth = this.donation.amount / 12;
                    this.donationPerhalfYear = this.donation.amount / 2;
                    this.donationPerYear = this.donation.amount;
                    break;
                case 'halfyearly':
                    this.donationPerMonth = this.donation.amount / 6;
                    this.donationPerhalfYear = this.donation.amount;
                    this.donationPerYear = this.donation.amount * 2;
                    break;
                case 'quarterly':
                    this.donationPerMonth = this.donation.amount / 3;
                    this.donationPerhalfYear = this.donation.amount * 2;
                    this.donationPerYear = this.donation.amount * 4;
                    break;
                case 'monthly':
                    this.donationPerMonth = this.donation.amount;
                    this.donationPerhalfYear = this.donation.amount * 6;
                    this.donationPerYear = this.donation.amount * 12;
                    break;
            }
        }
        else {
            // the donation is a one time donation
            this.rythmText = this.translate.instant('rythm.one_time.label');
        }

        let amount = this.donation.amount;
        let extraAmount = this.donation.extraAmount;
        const membershipPercent = this.optionService.getOption('extra_amount_unit') === 'percent';
        // if we have a membership widget, manipulate the amount based on the selected rythm
        if (this.projectType === 'membership' && this.donation.recurringRythm) {
            switch (this.donation.recurringRythm) {
                case 'yearly':
                    amount = this.donation.amount * 12;
                    if (membershipPercent) {
                      extraAmount = this.donation.extraAmount * 12;
                    }
                    break;
                case 'halfyearly':
                    amount = this.donation.amount * 6;
                    if (membershipPercent) {
                      extraAmount = this.donation.extraAmount * 6;
                    }
                    break;
                case 'quarterly':
                    amount = this.donation.amount * 3;
                    if (membershipPercent) {
                      extraAmount = this.donation.extraAmount * 3;
                    }
                    break;
            }
        }

        // add extra amount to the placeholder if set
        let amountPlaceholder: string | null = this.decimalPipe.transform(amount, '1.2-2');
        if (this.donation.extraAmount > 0) {
            amountPlaceholder += ' + ' + this.decimalPipe.transform(extraAmount, '1.2-2');
        }

        return {
            amount: amountPlaceholder,
            currencySymbol: this.donation.currencySymbol,
            donationPerMonth: this.donationPerMonth,
            donationPerYear: this.donationPerYear,
            donationPerhalfYear: this.donationPerhalfYear,
            rythm: this.rythmText
        }
    }

}
